/*
  Controls the popup hierarchy of the following apps
  - One Trust
  - Global-e
  - Klaviyo
 */

const POPUP_HIERARCHY_INIT_TIME = Date.now()
const INITIAL_LOAD_BUFFER = 1000
const TIMEOUT_TO_DISCONNECT_OBSERVER = 10000

let HAS_SCROLLED_TO_TOP_FOR_ONETRUST = false

/**
 * Check if element is visible on the page
 * @param {HTMLElement} elem : element to check visibility of
 * @returns boolean : true if element is visible, false otherwise
 */
const isElementVisible = (elem) => {
  if (!elem) return false
  const computedStyle = window.getComputedStyle(elem)
  return !!(
    elem.getClientRects().length &&
    computedStyle.visibility !== 'hidden' &&
    computedStyle.display !== 'none'
  )
}

/**
 * Look for open klaviyo toggle and fire click event
 * Need the delay to wait for button to be generated
 * @param {Node} klaviyoPopupParent : Klaviyo parent node
 */
const openKlaviyoFormFromOneTrust = (klaviyoPopupParent) => {
  setTimeout(() => {
    const klaviyoOpenButton =
      klaviyoPopupParent?.parentNode?.querySelector('span[role="button"]') ||
      null

    klaviyoOpenButton?.dispatchEvent(new Event('click'))
  }, 1000)
}

/**
 *  Disconnect the hierarchy observer and clean up any styles
 * @param {MutationObserver} hierarchyObserver : Mutation Observer to disconnect
 * @param {Object} param1  : Object containing the popups and relevant elements to clean up styles on
 */
const disconnectHierarchyObserver = (
  hierarchyObserver,
  { globalePopup, oneTrustPopup, klaviyoPopupParent }
) => {
  hierarchyObserver.disconnect()
  globalePopup?.classList.remove('popup-force-hide')
  oneTrustPopup?.classList.remove('popup-force-hide')
  klaviyoPopupParent?.classList.remove('popup-force-hide')
  HAS_SCROLLED_TO_TOP_FOR_ONETRUST = true
}

/**
 * Main function to run on a mutation
 * @param {Object} hierarchyObserver : Mutation Observer
 */
const hierarchyObserverMutationCallback = (hierarchyObserver) => {
  const bodyElemTarget = document.documentElement || document.body
  const timeSinceInit = Date.now() - POPUP_HIERARCHY_INIT_TIME

  // Query selectors for the popups and relevant elements
  const globalePopup = document.querySelector('#md-app-embed__modal')
  const oneTrustPopup = document.querySelector('#onetrust-consent-sdk')
  const oneTrustPopupBanner = document.querySelector('#onetrust-banner-sdk')
  const oneTrustAcceptButton = document.getElementById(
    'onetrust-accept-btn-handler'
  )
  const klaviyoPopupTarget =
    document.querySelector('div[aria-label="POPUP Form"].needsclick') || null
  const klaviyoPopupParent = klaviyoPopupTarget?.parentNode || null

  try {
    // Control Global E
    const isGlobalePopupVisible = isElementVisible(globalePopup)

    // Only hide if Global-e is not visible, and after a short buffer to allow it to initialize and show if needed, to prevent hiding it on initial load
    globalePopup?.classList.toggle(
      'popup-force-hide',
      !isGlobalePopupVisible && timeSinceInit > INITIAL_LOAD_BUFFER
    )

    // Control One Trust
    const isOneTrustVisible = isElementVisible(oneTrustPopupBanner)

    // to fix scrolling to bottom when OneTrust appears specifically on Safari iOS
    if (isOneTrustVisible && HAS_SCROLLED_TO_TOP_FOR_ONETRUST === false) {
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 100)
      HAS_SCROLLED_TO_TOP_FOR_ONETRUST = true
    }

    // Set up event to reset scroll position when onetrust is accepted, extra safeguard for Safari iOS where scroll can jump to bottom when accepting OneTrust
    if (
      oneTrustAcceptButton !== null &&
      !oneTrustAcceptButton.hasAttribute('data-klaviyo-scroll-trigger-init')
    ) {
      oneTrustAcceptButton.addEventListener('click', () => {
        const scrollTop = window.scrollY || bodyElemTarget.scrollTop
        const viewportHeight = window.innerHeight
        const documentHeight = bodyElemTarget.scrollHeight
        const isNearBottom =
          scrollTop + viewportHeight >= documentHeight - viewportHeight / 2
        if (isNearBottom) {
          window.scrollTo(0, 0)
        }
      })
      oneTrustAcceptButton.setAttribute(
        'data-klaviyo-scroll-trigger-init',
        true
      )
    }

    // Hide OneTrust if it's not visible, or if Global-e is visible, or during initial load buffer to prevent hiding on initial load
    oneTrustPopup?.classList.toggle(
      'popup-force-hide',
      isGlobalePopupVisible || timeSinceInit <= INITIAL_LOAD_BUFFER
    )

    // Control Klaviyo
    const isParentBody = klaviyoPopupParent === bodyElemTarget
    // Hide Klaviyo if Global-e or OneTrust is visible
    // or if the parent is body (indicating it's not in an iframe and likely not loaded properly)
    // or during initial load buffer to prevent hiding on initial load

    klaviyoPopupParent?.classList.toggle(
      'popup-force-hide',
      ((isGlobalePopupVisible || isOneTrustVisible) && !isParentBody) ||
        timeSinceInit <= INITIAL_LOAD_BUFFER + 250
    )

    // Set up event to open Klaviyo when onetrust is accepted
    if (
      oneTrustAcceptButton &&
      klaviyoPopupParent &&
      !oneTrustAcceptButton.hasAttribute('data-klaviyo-trigger-init')
    ) {
      oneTrustAcceptButton.setAttribute('data-klaviyo-trigger-init', true)
      oneTrustAcceptButton.addEventListener('click', () => {
        openKlaviyoFormFromOneTrust(klaviyoPopupParent)
      })
    }

    // Disconnect observer if onetrust and global-e aren't visible, and after load buffer
    const loadBuffer = timeSinceInit > TIMEOUT_TO_DISCONNECT_OBSERVER
    if (!isOneTrustVisible && !isGlobalePopupVisible && loadBuffer) {
      disconnectHierarchyObserver(hierarchyObserver, {
        globalePopup,
        oneTrustPopup,
        klaviyoPopupParent,
      })
    }
  } catch (error) {
    console.error('Error in hierarchyObserverMutationCallback:', error)
    disconnectHierarchyObserver(hierarchyObserver, {
      globalePopup,
      oneTrustPopup,
      klaviyoPopupParent,
    })
  }
}

/**
 * Set up observer
 */
const popupHierarchyControl = () => {
  const bodyElemTarget = document.documentElement || document.body
  const hierarchyObserverConfig = {
    attributes: true,
    childList: true,
    subtree: true,
  }
  const hierarchyObserver = new MutationObserver(() =>
    requestAnimationFrame(() =>
      hierarchyObserverMutationCallback(hierarchyObserver)
    )
  )
  hierarchyObserver.observe(bodyElemTarget, hierarchyObserverConfig)
}

/**
 * Call popupHierarchyControl after DOM content load
 */
document.addEventListener('DOMContentLoaded', popupHierarchyControl)
